import { ChannelOutcome } from '../alert_scenarios/types'
import { ExecutionRow } from './types'

export type Channel = 'sms' | 'email'

export const CHANNELS: Channel[] = ['sms', 'email']
export const CHANNEL_NAMES: Record<Channel, string> = { sms: 'SMS', email: 'Email' }
const RECIPIENT_NOUNS: Record<Channel, [string, string]> = {
  sms: ['number', 'numbers'],
  email: ['address', 'addresses']
}

export interface ChannelDelivery {
  recipients: string[]
  status: ChannelOutcome | null
}

export const channelDelivery = (execution: ExecutionRow, channel: Channel): ChannelDelivery =>
  channel === 'sms'
    ? { recipients: execution.phone_numbers, status: execution.sms_status }
    : { recipients: execution.email_addresses, status: execution.email_status }

export const nobodyNotified = (execution: ExecutionRow): boolean =>
  CHANNELS.every((channel) => channelDelivery(execution, channel).recipients.length === 0)

// One outcome covers the whole channel: a single number that did not answer fails the SMS leg for
// every recipient, so the wording never pins a failure on one number.
export const deliveryDescription = (channel: Channel, { recipients, status }: ChannelDelivery): string => {
  const name = CHANNEL_NAMES[channel]
  const [singular, plural] = RECIPIENT_NOUNS[channel]
  const count = recipients.length

  switch (status) {
    case 'sent': return `${name}: sent to ${count} ${count === 1 ? singular : plural}`
    case 'failed': return `${name}: failed — at least one ${singular} did not receive it`
    case 'skipped': return `${name}: not sent — delivery is disabled in this environment`
    case 'not_requested': return `${name}: not sent — the channel had no recipients or message`
    case null: return `${name}: delivery outcome not recorded`
  }
}

export const channelBadgeClass = (channel: Channel, status: ChannelOutcome | null): string => {
  if (status === 'failed') return 'badge bg-danger'
  if (status !== 'sent') return 'badge bg-secondary'

  return channel === 'sms' ? 'badge bg-primary' : 'badge bg-info text-dark'
}
